import React, { createContext, useContext } from 'react';
import { type CategoryTreeBuildDto } from '@/api';
import { type CategoryTree } from '@/shared/category/type';

/**
 * BuildAiModal에서 사용하는 카테고리 관련 컨텍스트
 * Props drilling을 제거하기 위해 모달 레벨에서 제공됩니다.
 */
export interface BuildAiCategoryContextValue {
  /** 과목 트리 (성취기준 필터용) */
  subjectTree: CategoryTreeBuildDto[];
  /** 학교 정보 트리 (학교급/학년 계층 구조) */
  schoolInfoTree: CategoryTree;
  /** 초기 학교 정보 ID (학교급+학년 포함) */
  initialSchoolInfoId?: string;
  /** 초기 과목 ID (categoryGroupId) */
  initialSubjectId?: string;
}
const BuildAiCategoryContext = createContext<BuildAiCategoryContextValue | undefined>(undefined);
interface BuildAiCategoryProviderProps {
  children: React.ReactNode;
  value?: BuildAiCategoryContextValue;
}

/**
 * BuildAiModal의 카테고리 정보를 제공하는 Context Provider
 *
 * @example
 * <BuildAiCategoryProvider value={{ subjectTree, schoolInfoTree, schoolTypeId, gradeId, subject }}>
 *   <BuildAiModalComponent />
 * </BuildAiCategoryProvider>
 */
export const BuildAiCategoryProvider = ({
  children,
  value
}: BuildAiCategoryProviderProps) => {
  return <BuildAiCategoryContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="BuildAiCategoryProvider" data-sentry-source-file="BuildAiCategoryProvider.tsx">
      {children}
    </BuildAiCategoryContext.Provider>;
};

/**
 * BuildAiModal의 카테고리 정보를 사용하는 훅
 *
 * @throws {Error} BuildAiCategoryProvider 외부에서 사용된 경우
 *
 * @example
 * const { schoolInfoTree, subjectTree, initialSchoolInfoId, initialSubjectId } = useBuildAiCategory();
 */
export const useBuildAiCategory = (): BuildAiCategoryContextValue => {
  const context = useContext(BuildAiCategoryContext);
  if (context === undefined) {
    throw new Error('useBuildAiCategory must be used within BuildAiCategoryProvider');
  }
  return context;
};